/// <amd-module name="Favorites/Picker/ArtifactPickerProvider" />
import * as React from "react";

import { EventGroup } from "VSSUI/Utilities/Event";

import { IPickListAction, IPickListGroup, IPickListIndicator, IPickListItem, IPickListPivot } from "VSSUI/Components/PickList/PickList.Props";
import { IItem, IItemIndicatorProps, IItemState, Item } from "VSSUI/ItemIndicator";
import { IItemPickerProvider, PICKER_CHANGE_EVENT } from "VSSUI/PickList";
import { IVssIconProps } from "VSSUI/VssIcon";

import { IObservableArrayEventArgs } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";

import { ArtifactScope, Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesCollection, IFavoritesService } from "Favorites/Common/FavoritesService";
import * as FavoriteResources from "Favorites/Common/Resources";

export interface IArtifactPickListItem<TArtifact> extends IPickListItem {
    artifact: TArtifact;
}

export interface IFavoritesContext {
    /**
     * Type of the favorited artifact (query, team, repository, etc.)
     */
    artifactType: string;

    /**
     * Describes the scope a favorited Artifact resides in. e.g. A team project
     */
    artifactScope: ArtifactScope;

    /**
     * Optional (already-fetched) collection of favorites
     */
    favorites?: IFavoritesCollection;
}

/**
 * Defines only the functionality of a favorites service that this picker requires.
 */
export interface FavoritePickerDataService {
    /**
     * Whether the service supports to query favorites with extended details (probably slower).
     */
    canFetchExtendedDetails: () => boolean;
    /**
     * Describes if the current user has general read and write permissions on the Favorites Service.
     */
    canUseFavorites(): boolean;

    /**
     * Get favorites of the given artifact type
     */
    getFavorites(
        artifactType: string,
        artifactScopeType?: string | undefined,
        artifactScopeId?: string | undefined,
        includeExtendedDetails?: boolean | undefined
    ): Promise<IFavoritesCollection>;

    /**
     * Add (favorite) the given item
     */
    addFavorite(favorite: Favorite): Promise<Favorite>;

    /**
     * Remove (un-favorite) the given favorite item
     */
    removeFavorite(favorite: Favorite): Promise<void>;
}

/**
 * @deprecated use IArtifactDropdownProviderOptions instead
 */

export interface IArtifactPickerProviderOptions<TArtifact> {
    /**
     * The current page context
     */
    pageContext: IVssPageContext;

    /**
     * Delegate to fetch the set of artifacts to show in the picker.
     * @param groupKey If a group needs an extra fetch (isLoading = true), this call retrieves these additional items.
     * New items will be joined with previous ones.
     */
    getArtifacts: (groupKey?: string) => TArtifact[] | Promise<TArtifact[]>;

    /**
     * Executed when an artifact is clicked.
     *
     * @returns True if the event was handled, preventing the navigate event if this item has an href.
     */
    onArtifactClicked?: (artifact: TArtifact) => void | boolean;

    /**
     * Item controlled by visible favorite indicator in the item that can be dropped down.
     * If specified, favorite star is displayed which can be toggled.
     */
    selectedArtifact?: TArtifact;

    /**
     * Items to display in the search result area using the search text specified.
     * If not specified, search text is not displayed.
     * If picker uses pivots, the current one is provided so search can be specific to it.
     * @param continuationToken Used for pagination, results will continue from the next page.
     */
    getSearchResults?: (
        searchText: string,
        pivotKey?: string,
        continuationToken?: string
    ) => TArtifact[] | Promise<TArtifact[] | undefined> | undefined;

    /**
     * If provided, supports paginated search by retrieving a continuation token for these results.
     */
    getSearchResultsContinuationToken?: (results: TArtifact[]) => string | undefined;

    /**
     * Optional delegate to use to sort artifacts.
     */
    artifactComparer?: (item1: TArtifact, item2: TArtifact) => number;

    /**
     * Placeholder text for search box
     */
    searchTextPlaceholder?: string;

    /**
     * Optional group header to display for search results
     */
    searchResultsGroupName?: string;

    /**
     * Optional message to display when the search results are still loading.
     */
    searchResultsLoadingText?: string;

    /**
     * Optional message to display when there are no search results
     */
    searchNoResultsText?: string;

    /**
     * Click handler for browse all action.
     * If not specified, 'browse all' action is not displayed.
     */
    onBrowseAllClick?: () => void;

    /**
     * Optional property to use custom text for the "browse all" action.
     */
    browseAllText?: string;

    /**
     * Other optional actions appearing above browse all.
     */
    otherActions?: IPickListAction[];

    /**
     * Get the name to display as text for the given artifact in the pick list
     */
    getArtifactName: (item: TArtifact) => string;

    /**
     * Get the artifactId from the given item (used for matching the artifact with a Favorite item and for the item's key)
     */
    getArtifactId: (item: TArtifact) => string;

    /**
     * Optional delegate to get the URL for the given item which will cause a hyperlink to be used for the item's entry.
     */
    getArtifactHref?: (item: TArtifact) => string;

    /**
     * Gets the icon to show to the left of the specified item.
     */
    getArtifactIcon?: (item: TArtifact) => IVssIconProps;

    /**
     * Optional delegate to get a pick list group id for a given artifact.
     */
    getArtifactListGroupId?: (item: TArtifact) => string;

    /**
     * Optional delegate to return a custom text value to use for the root component's selected-value text.
     * By default, the text (via getArtifactName) of the selected artifact is used.
     * This can be leveraged if you want the text used in the (dropdown-able) selected item to be different for a given item
     * compared to what shows up in the picker dropdown.
     */
    getRootItemText?: (selectedItem: TArtifact) => string;

    /**
     * Text message or a react element to display when there are no items available.
     */
    getNoItemsMessage?: () => string | JSX.Element;

    /**
     * Custom text to show when items are still being (slowly) loaded. Defaults to "Loading...".
     */
    loadingText?: string;

    /**
     * Additional indicators to show in the pick list dropdown before the favorites "star" indicator at the end.
     */
    dropdownIndicators?: IPickListIndicator[];

    /**
     * Additional indicators to show next to the selected item before the favorites "star" indicator.
     */
    selectedItemIndicators?: IPickListIndicator[];

    /**
     * If true, don't show the favorite (star) indicator next to the selected item.
     */
    hideFavoriteSelectedItemIndicator?: boolean;

    /**
     * Groups of items to render.
     */
    groups?: IPickListGroup[] | Promise<IPickListGroup[]>;

    /**
     * the pivots to render.
     */
    pivots?: IPickListPivot[];

    /**
     * If true, search will only return items in the current pivot.
     * Otherwise, items from all pivots are mixed in the search results.
     */
    limitSearchToPivot?: boolean;

    /**
     * Formattable string that contains an ariaLabel to use for the component.
     * If there is an {0} argument available, will append the text of the dropdown,
     * which is the currently selected items, if any.
     */
    ariaLabelFormat?: string;

    /**
     * Optional context for favorites to use. This will enable consumers to subscribe
     * favorite operation like toggle themselves as well as favorite picker.
     *
     * If not set, then favorites are not leveraged.
     */
    favoritesContext?: IFavoritesContext;

    /**
     * Optional service to use for data access in place of the standard favorites service.
     * Use it if your picker uses a non-standard favoriting layer (e.g. GitRefFavorite)
     */
    favoritesService?: FavoritePickerDataService;

    /**
     * Optional delegate to convert a Favorite item to an artifact. If specified, all favorite items will be shown
     * in the dropdown immediately, even if the initial item fetch has not yet completed. Favorite items will be
     * fully resolved if/when a matching item is returned from getArtifacts.
     */
    getArtifactFromFavorite?: (favoriteItem: Favorite) => TArtifact;

    /**
     * Optional delegate to create a favorite entry from the given artifact. If specified, artifacts that
     * don't yet exist in the artifact store can still be favorited.
     */
    getFavoriteFromArtifact?: (artifact: TArtifact) => Favorite | undefined;

    /**
     * Optional delegate to be called when an error is encountered by favoriting/unfavoriting an item
     * in the picker.
     */
    onFavoriteLoadError?: (error: any) => void;

    /**
     *  The pivot id associated with the favorite group. This needs to be provided
     *  if pivots are provided to the artifact provider.
     */
    favoriteGroupPivotId?: string;

    /**
     *  The pivot id associated with the default group. This needs to be provided
     *  if pivots are provided to the artifact provider.
     */
    defaultGroupPivotId?: string;

    /**
     * Header text for the default group
     */
    defaultGroupHeader?: string;

    /**
     * Header text for the favorites group
     */
    favoriteGroupHeader?: string;

    /**
     * Optional boolean to control searchability
     */
    isSearchable?: boolean;

    /**
     * Optional method to allow using more text than item.name when filtering.
     */
    onFilterItemByText?(searchText: string, item: IPickListItem, data: any): boolean;

    /**
     * Optional method to allow using more text than item.name when filtering.
     */
    onFilterItemByText?(searchText: string, item: IPickListItem, data: any): boolean;
}

/**
 * @deprecated use IArtifactDropdownProvider instead
 */
export interface IArtifactPickerProvider<TArtifact = {}> extends IItemPickerProvider<TArtifact> {
    /**
     * Sets the selected item in the picker.
     */
    setSelectedItem: (item: TArtifact) => void;

    /**
     * Disposes all resources associated with the provider
     */
    dispose: () => void;
}

/**
 * Provides items for a picker which shows artifacts with favorites support.
 * @deprecated use ArtifactDropdownProviderBase instead
 */
export class ArtifactPickerProviderBase<TArtifact = {}> implements IArtifactPickerProvider<TArtifact> {
    /**
     * The currently selected item in the picker.
     */
    public get selectedItem(): TArtifact | undefined {
        if (!this._handledInitialSelection) {
            this._handledInitialSelection = true;
            this._selectedItem = this._options.selectedArtifact;
        }
        return this._selectedItem;
    }

    /**
     * Indicators that will be displayed on the right side of the selected item in the root component.
     */
    public get selectedItemIndicators(): IPickListIndicator[] {
        if (this._favoriteIndicator && !this._options.hideFavoriteSelectedItemIndicator) {
            return this._options.selectedItemIndicators
                ? [...this._options.selectedItemIndicators, this._favoriteIndicator]
                : [this._favoriteIndicator];
        } else {
            return this._options.selectedItemIndicators || [];
        }
    }

    /**
     * Indicators that will be displayed on the right side of each item in the dropdown
     */
    public get dropdownIndicators(): IPickListIndicator[] {
        if (this._favoriteIndicator) {
            return this._options.dropdownIndicators ? [...this._options.dropdownIndicators, this._favoriteIndicator] : [this._favoriteIndicator];
        } else {
            return this._options.dropdownIndicators || [];
        }
    }

    /**
     * Definitions for groups
     */
    public get groups(): IPickListGroup[] {
        let optionsGroups: IPickListGroup[] | undefined;
        if (this._options.groups && isPromise(this._options.groups)) {
            // Use _resolvedGroups only when groups is a promise, to avoid resolving the it multiple times
            if (!this._resolvedGroups) {
                this._options.groups.then(value => {
                    this._resolvedGroups = value;
                    EventGroup.raise(this, PICKER_CHANGE_EVENT);
                });

                this._resolvedGroups = this._getPlaceholderGroups();
            }

            optionsGroups = this._resolvedGroups;
        } else {
            optionsGroups = this._options.groups;
        }

        // Clone the built-in groups before returning them so any further modifications to them
        // made by this class (for example, setting _defaultGroup.loading to false) don't leak out to
        // refs holding onto the objects returned by this method (for example, PickList when comparing
        // its new groups props against its previous props to determine if there was a change).
        const clonedBuiltInGroups = this._builtInGroups.map(g => {
            return { ...g };
        });

        return [...clonedBuiltInGroups, ...(optionsGroups || [])];
    }

    private _options: IArtifactPickerProviderOptions<TArtifact>;

    private _artifactsPromise: TArtifact[] | Promise<TArtifact[]> | undefined;
    private _resolvedArtifacts: TArtifact[] | undefined;
    private _artifactsById: { [artifactId: string]: TArtifact } = {};
    private _selectedItem: TArtifact | undefined;
    private _handledInitialSelection: boolean;
    private _disposed = false;

    private _resolvedGroups: IPickListGroup[];

    private _favoritesService: FavoritePickerDataService;
    private _favoritesCollection: IFavoritesCollection | undefined;
    private _favoriteIndicator: IPickListIndicator;
    private _favoriteGroup: IPickListGroup;
    private _builtInGroups: IPickListGroup[] = [];
    private _defaultGroup: IPickListGroup;
    private _favoritesByArtifactId: { [artifactId: string]: Favorite } = {};
    private _favoriteGroupItems: { [id: string]: boolean } = {};
    private _favoriteIndicatorItems: { [id: string]: IItem } = {};
    private _extendedFavoritesLoaded = false;
    private _lastSearchResults: TArtifact[];
    private _lastSearchResultsContinuationToken: string | undefined;

    /**
     * Gets the search result for the specified search text
     */
    public onSearch: (searchText: string, items: TArtifact[], pivotKey: string, resultsToContinue: TArtifact[]) => TArtifact[] | Promise<TArtifact[]>;

    constructor(options?: IArtifactPickerProviderOptions<TArtifact>) {
        if (options) {
            this.initialize(options);
        }
    }

    public dispose(): void {
        if (this._favoritesCollection) {
            if (this._options.favoritesContext!.favorites === this._favoritesCollection) {
                this._favoritesCollection.dispose();
            }
            this._favoritesCollection = undefined;
        }

        this._disposed = true;
    }

    public getTitleTextForItem(item: TArtifact): string {
        return this._options.getRootItemText ? this._options.getRootItemText(item) : item && this._options.getArtifactName(item);
    }

    /**
     * Get list of items to be displayed in favorites section
     */
    public getItems = (): TArtifact[] | Promise<TArtifact[]> => {
        if (!this._artifactsPromise) {
            this._artifactsPromise = this._options.getArtifacts();

            const process = (artifacts: TArtifact[]) => {
                this._resolvedArtifacts = this.processResolvedArtifacts(artifacts);
                this._defaultGroup.isLoading = false;
                EventGroup.raise(this, PICKER_CHANGE_EVENT);
            };

            if (isPromise(this._artifactsPromise)) {
                this._artifactsPromise.then(process);
            } else {
                process(this._artifactsPromise);
            }
        }

        if (!this._options.favoritesContext) {
            // Not using favorites, just return the artifacts
            return this._resolvedArtifacts || [];
        }

        // takes care of deleted items, name/scope updates for the picker text and on the dropdown being opened.
        this._loadFavoriteExtendedData();

        if (!this._favoritesCollection) {
            // Favorites not loaded yet. Don't return items until we at least have favorites since
            // we are going to display the favorites as the first group.
            return [];
        }

        // Sort favorites and reset the lookup
        const favorites: Favorite[] = this._favoritesCollection.value.filter(f => !f.artifactIsDeleted);
        this._favoritesByArtifactId = {};
        this._favoriteGroupItems = {};

        if (!favorites.length) {
            // No favorites -- return the artifacts
            return this._resolvedArtifacts || [];
        }

        // Populate the favorite item lookup by artifact id
        for (const favorite of favorites) {
            this._favoritesByArtifactId[favorite.artifactId] = favorite;
            this._favoriteGroupItems[favorite.artifactId] = true;
        }

        let items: TArtifact[] = [];

        if (this._resolvedArtifacts) {
            // Add favorite items first
            for (const favorite of favorites) {
                let resolvedArtifact = this._artifactsById[favorite.artifactId];
                if (!resolvedArtifact && this._options.getArtifactFromFavorite) {
                    resolvedArtifact = this._options.getArtifactFromFavorite(favorite);
                }
                if (resolvedArtifact) {
                    items.push(resolvedArtifact);
                }
            }

            // Add remaining items which are NOT favorites
            for (const artifact of this._resolvedArtifacts) {
                const artifactId = this._options.getArtifactId(artifact);
                if (!this._favoritesByArtifactId[artifactId]) {
                    items.push(artifact);
                }
            }

            if (this._options.artifactComparer) {
                items = items.sort(this._options.artifactComparer);
            }
        } else if (this._options.getArtifactFromFavorite) {
            // Artifacts not fetched yet, create an artifact from each favorite item.
            for (const favorite of favorites) {
                const resolvedFavorite = this._options.getArtifactFromFavorite(favorite);
                if (resolvedFavorite) {
                    items.push(resolvedFavorite);
                }
            }
            if (this._options.artifactComparer) {
                items = items.sort(this._options.artifactComparer);
            }
        }

        return items;
    };

    public onGroupLoadRequest = (groupKey: string, lastCallItemsCount: number = 0): Promise<TArtifact[]> => {
        if (groupKey === "favorites" || groupKey === "default") {
            return Promise.resolve([]);
        }

        const artifacts = this._options.getArtifacts(groupKey);

        const process = (artifacts: TArtifact[]) => {
            const previousCount = this._resolvedArtifacts && this._resolvedArtifacts.length;
            this._resolvedArtifacts = this.processResolvedArtifacts(artifacts);
            if (
                artifacts.length > lastCallItemsCount &&
                this._resolvedArtifacts.length === previousCount &&
                this.groups.some(g => g.name === groupKey && Boolean(g.isLoading))
            ) {
                // We didn't load anything new, and given that the group is not still fully loaded,
                // we assume that every retrieved item was already in another group, so let's load another page.
                // Only while fetched items are strictly greater, which prevents infinite recursion.
                return this.onGroupLoadRequest(groupKey, artifacts.length);
            }

            EventGroup.raise(this, PICKER_CHANGE_EVENT);

            return this._resolvedArtifacts;
        };

        if (isPromise(artifacts)) {
            return artifacts.then(process);
        } else {
            return Promise.resolve(process(artifacts));
        }
    };

    /**
     * Returns a PickListItem for the specified item
     */
    public getListItem = (item: TArtifact): IArtifactPickListItem<TArtifact> => {
        const { getArtifactName, getArtifactId, getArtifactListGroupId, getArtifactHref, getArtifactIcon } = this._options;

        const listItem: IArtifactPickListItem<TArtifact> = {
            artifact: item,
            name: getArtifactName(item),
            key: getArtifactId(item)
        };

        if (this._getFavoriteItem(getArtifactId(item))) {
            listItem.groupKey = "favorites";
        } else if (getArtifactListGroupId) {
            listItem.groupKey = getArtifactListGroupId(item);
        }
        if (!listItem.groupKey) {
            listItem.groupKey = "default";
        }

        if (getArtifactIcon) {
            listItem.iconProps = getArtifactIcon(item);
        }
        if (getArtifactHref) {
            listItem.href = getArtifactHref(item);
        }

        return listItem;
    };

    /**
     * Sets the selected item for the existing favorite picker.
     */
    public setSelectedItem(artifact: TArtifact): void {
        this._selectedItem = artifact;

        if (artifact) {
            // set indicator state for the new item
            const artifactId = this._options.getArtifactId(artifact);
            this._updateIndicatorItem(artifactId, !!this._getFavoriteItem(artifactId), true);
        }
    }

    public onSelectedItemChanged = (artifact: TArtifact): void | boolean => {
        this.setSelectedItem(artifact);

        const { onArtifactClicked, getArtifactHref } = this._options;
        if (onArtifactClicked) {
            return onArtifactClicked(artifact);
        }

        if (getArtifactHref) {
            return onClickFPS(this._options.pageContext, getArtifactHref(artifact), true, undefined, { telemetrySource: "ArtifactPickerProvider" });
        }
    };

    public onHasMoreSearchResults = (searchResults: any[]): boolean => {
        return searchResults === this._lastSearchResults && Boolean(this._lastSearchResultsContinuationToken);
    };

    protected initialize(options: IArtifactPickerProviderOptions<TArtifact>) {
        this._options = options;

        this.initializeDefaultGroup();

        if (options.getSearchResults) {
            this.onSearch = (searchText: string, items: TArtifact[], pivotKey: string, resultsToContinue: TArtifact[]) => {
                const continuationToken =
                    (resultsToContinue && resultsToContinue === this._lastSearchResults && this._lastSearchResultsContinuationToken) || undefined;

                const results = options.getSearchResults!(searchText, pivotKey, continuationToken);

                const process = (newResults: TArtifact[] | undefined) => this.getUniqueSearchResults(newResults, Boolean(continuationToken));

                if (isPromise(results)) {
                    return results.then(process);
                } else {
                    return process(results);
                }
            };
        }

        const favoritesContext = options.favoritesContext;
        this._favoritesService = options.favoritesService || options.pageContext.getService<IFavoritesService>("IFavoritesService");
        if (favoritesContext && this._favoritesService.canUseFavorites()) {
            this.initializeFavoriteGroup();

            if (favoritesContext.favorites) {
                this._onFavoritesLoaded(favoritesContext.favorites);
            } else if (!this._options.hideFavoriteSelectedItemIndicator) {
                // Pre-load the favorites (without extended details) in order to ensure we have the necessary
                // favorite information for the selected item's favorite indicator.
                this._loadFavorites();
            }

            this._favoriteIndicator = {
                getItemIndicator: (item: TArtifact) => {
                    const artifactId = options.getArtifactId(item);
                    if (!artifactId) {
                        return null;
                    }

                    const favoriteItem: Favorite = this._getFavoriteItem(artifactId);
                    if (!favoriteItem && (!this._options.getFavoriteFromArtifact || !this._options.getFavoriteFromArtifact(item))) {
                        // Not in the favorite store, and we don't have the ability to create a favorite
                        // from this artifact, so don't show an indicator.
                        return null;
                    }

                    const indicatorItem: IItem = this._updateIndicatorItem(artifactId, !!favoriteItem, true);

                    return {
                        title: "",
                        item: indicatorItem,
                        onClick: (ev: React.MouseEvent<HTMLButtonElement>, indicatorItem: IItem) => {
                            // Get/create the favorite item
                            let favorited = false;
                            let clickedFavoriteItem: Favorite | undefined = this._getFavoriteItem(artifactId);
                            if (clickedFavoriteItem) {
                                favorited = true;
                            } else {
                                clickedFavoriteItem = this._options.getFavoriteFromArtifact!(item);
                                if (!clickedFavoriteItem) {
                                    return;
                                }
                            }

                            // perform actual toggle through favorite service
                            if (favorited) {
                                this._favoritesService.removeFavorite(clickedFavoriteItem);
                            } else {
                                this._favoritesService.addFavorite(clickedFavoriteItem);
                            }
                        }
                    } as IItemIndicatorProps;
                }
            };
        }
    }

    protected forgetCachedArtifacts = (): void => {
        this._artifactsPromise = undefined;
        this._artifactsById = {};
        this._resolvedArtifacts = undefined;
    };

    protected _loadFavorites(includeExtendedData: boolean = false): Promise<IFavoritesCollection | undefined> {
        if (this._extendedFavoritesLoaded) {
            return Promise.resolve(undefined);
        }

        const favoritesContext = this._options.favoritesContext!;
        const promise = this._favoritesService
            .getFavorites(favoritesContext.artifactType, favoritesContext.artifactScope.type, favoritesContext.artifactScope.id, includeExtendedData)
            .then(this._onFavoritesLoaded, this._favoritesErrorDelegate);

        this._extendedFavoritesLoaded = includeExtendedData;

        return promise;
    }

    private processResolvedArtifacts(artifacts: TArtifact[]): TArtifact[] {
        // Build hash of resolved artifacts by artifact id
        // It will replace duplicates with new version
        for (const artifact of artifacts) {
            this._artifactsById[this._options.getArtifactId(artifact)] = artifact;
        }

        let allArtifacts = Object.keys(this._artifactsById).map(key => this._artifactsById[key]);

        if (this._options.artifactComparer) {
            allArtifacts = allArtifacts.sort(this._options.artifactComparer);
        }

        return allArtifacts;
    }

    private _getPlaceholderGroups(): IPickListGroup[] {
        const placeholderGroups: IPickListGroup[] = [];
        if (this.pivots) {
            for (const pivot of this.pivots) {
                placeholderGroups.push({
                    key: "placeholder",
                    isLoading: true,
                    loadingMessage: this._options.loadingText,
                    pivotId: pivot.id
                });
            }
        } else {
            placeholderGroups.push({
                key: "placeholder",
                isLoading: true,
                loadingMessage: this._options.loadingText
            });
        }

        return placeholderGroups;
    }

    public get pivots(): IPickListPivot[] {
        return this._options.pivots || [];
    }

    public get limitSearchToPivot(): boolean {
        return this._options.limitSearchToPivot || false;
    }

    public get ariaLabelFormat(): string | undefined {
        return this._options.ariaLabelFormat;
    }

    public get onFilterItemByText(): ((searchText: string, item: IPickListItem, data: any) => boolean) | undefined {
        return this._options.onFilterItemByText;
    }

    /**
     * Optional list of actions to render after all items and groups
     */
    public get actions(): IPickListAction[] {
        const { browseAllText, onBrowseAllClick, otherActions } = this._options;

        const actions: IPickListAction[] = otherActions ? [...otherActions] : [];
        if (onBrowseAllClick) {
            // add browse all action to the end if a click handler specified
            actions.push({
                onClick: onBrowseAllClick,
                name: browseAllText || FavoriteResources.FavoriteItemPickerBrowseAllText,
                iconProps: {
                    iconName: "Home"
                }
            });
        }

        return actions;
    }

    /**
     * Whether or not the search box should be shown for the PickList
     */
    public get isSearchable(): boolean {
        return this._options.isSearchable === undefined ? true : this._options.isSearchable;
    }

    /**
     * Placeholder text used in the search TextField
     */
    public get searchTextPlaceholder(): string | undefined {
        return this._options.searchTextPlaceholder;
    }

    /**
     * Text to display when results are being loaded
     */
    public get searchResultsGroupName(): string | undefined {
        return this._options.searchResultsGroupName;
    }

    /**
     * Text to display when search results are loading but not yet ready.
     */
    public get searchResultsLoadingText(): string | undefined {
        return this._options.searchResultsLoadingText;
    }

    /**
     * Text to display when there are no results (based on the current search criteria)
     */
    public get searchNoResultsText(): string | undefined {
        return this._options.searchNoResultsText;
    }

    public get noItemsText(): string | JSX.Element | undefined {
        if (this._favoritesService && !this._favoritesCollection) {
            // still loading the favorites
            return React.createElement(
                "span",
                { className: "vss-FavoriteItemPicker--noItemsMessage" },
                FavoriteResources.FavoriteItemPickerLoadingMessage
            );
        } else {
            let noFavoritesMessage: string | JSX.Element | undefined = undefined;
            if (typeof this._options.getNoItemsMessage === "function") {
                noFavoritesMessage = this._options.getNoItemsMessage();
            }
            return noFavoritesMessage;
        }
    }

    public get itemAriaDescription(): string {
        return FavoriteResources.FavoriteItemPickerAriaDescription;
    }

    private getUniqueSearchResults(results: TArtifact[] | undefined, isContinuing: boolean): TArtifact[] {
        const uniqueResults: TArtifact[] = isContinuing ? this._lastSearchResults : [];
        for (const result of results || []) {
            const artifactId = this._options.getArtifactId(result);
            if (!this._artifactsById[artifactId] && !this._favoritesByArtifactId[artifactId]) {
                uniqueResults.push(result);
            }
        }

        this._lastSearchResults = uniqueResults;
        this._lastSearchResultsContinuationToken =
            results && this._options.getSearchResultsContinuationToken && this._options.getSearchResultsContinuationToken(results);

        return uniqueResults;
    }

    private initializeDefaultGroup() {
        const builtInDefaultGroup = {
            key: "default",
            name: this._options.defaultGroupHeader,
            isLoading: true,
            loadingMessage: this._options.loadingText,
            pivotId: this._options.defaultGroupPivotId
        };

        // provided favorites group is only supported in synchronous mode
        const providedGroup = this._tryGetGroupSynchronously("default");
        if (providedGroup) {
            this._defaultGroup = providedGroup;
        } else {
            this._defaultGroup = builtInDefaultGroup;
            this._builtInGroups.push(builtInDefaultGroup);
        }
    }

    private initializeFavoriteGroup() {
        const builtInFavoritesGroup: IPickListGroup = {
            key: "favorites",
            name: this._options.favoriteGroupHeader,
            isLoading: true,
            loadingMessage: this._options.loadingText,
            pivotId: this._options.favoriteGroupPivotId
        };

        // provided favorites group is only supported in synchronous mode
        const providedGroup = this._tryGetGroupSynchronously("favorites");
        if (providedGroup) {
            this._favoriteGroup = providedGroup;
        } else {
            this._favoriteGroup = builtInFavoritesGroup;
            this._builtInGroups.unshift(builtInFavoritesGroup);
        }
    }

    private _tryGetGroupSynchronously(groupKey: string): IPickListGroup | undefined {
        const { groups } = this._options;
        if (groups && !isPromise(groups)) {
            return groups.filter(g => g.key === groupKey)[0];
        }
    }

    private _favoritesErrorDelegate = (error: any): undefined => {
        if (this._options.onFavoriteLoadError) {
            this._options.onFavoriteLoadError(error);
        }

        return undefined;
    };

    private _getFavoriteItem(artifactId: string): Favorite {
        const favoriteItem = this._favoritesByArtifactId[artifactId];
        if (!favoriteItem && this._favoritesCollection && !this._artifactsPromise) {
            // We haven't gone through getItems yet, but the favorites collection is available. Check there.
            return this._favoritesCollection.value.filter(favorite => favorite.artifactId === artifactId)[0];
        }
        return favoriteItem;
    }

    private _updateIndicatorItem(artifactId: string, favorited: boolean, suppressChangeEvent: boolean): IItem {
        const indicatorItems: { [id: string]: IItem } = this._favoriteIndicatorItems;
        const favoriteState = this._getIndicatorState(favorited, !!this._favoriteGroupItems[artifactId]);

        if (!(artifactId in indicatorItems)) {
            indicatorItems[artifactId] = new Item({
                initialState: favoriteState
            });
        } else {
            indicatorItems[artifactId].setState(favoriteState, suppressChangeEvent);
        }

        return indicatorItems[artifactId];
    }

    private _getIndicatorState(favorited: boolean, favoriteGroup: boolean): IItemState {
        return {
            title: "",
            isPressed: favorited,
            iconProps: {
                iconName: favorited ? "FavoriteStarFill" : "FavoriteStar",
                className: "vss-FavoriteItemPicker--starIcon ms-font-l" + (favoriteGroup || favorited ? "" : " not-initial-favorite"),
                title: favorited ? FavoriteResources.FavoriteStarFavoritedTitle : FavoriteResources.FavoriteStarUnfavoritedTitle
            }
        };
    }

    private _onFavoritesLoaded = (collection: IFavoritesCollection): IFavoritesCollection | undefined => {
        if (this._disposed) {
            // Already disposed when the favorites-fetch completed
            collection.dispose();
            return;
        } else if (this._favoritesCollection && this._favoritesCollection !== this._options.favoritesContext!.favorites) {
            // Dispose the previous collection
            this._favoritesCollection.dispose();
        }

        this._favoritesCollection = collection;
        this._favoriteGroup.isLoading = false;
        collection.subscribe(this._onFavoriteItemChanged);

        if (this.selectedItem) {
            // Update the favorite item state
            const artifactId = this._options.getArtifactId(this.selectedItem);
            this._updateIndicatorItem(artifactId, !!this._getFavoriteItem(artifactId), false);
        }

        EventGroup.raise(this, PICKER_CHANGE_EVENT);

        return collection;
    };

    private _onFavoriteItemChanged = (ev: IObservableArrayEventArgs<Favorite>, action?: string): void => {
        if (ev.addedItems) {
            for (const item of ev.addedItems) {
                this._favoritesByArtifactId[item.artifactId] = item;
                this._updateIndicatorItem(item.artifactId, true, false);
            }
        }
        if (ev.removedItems) {
            for (const item of ev.removedItems) {
                delete this._favoritesByArtifactId[item.artifactId];
                this._updateIndicatorItem(item.artifactId, false, false);
            }
        }
    };

    private _loadFavoriteExtendedData(): void {
        this._loadFavorites(true);
    }
}

/**
 * Provides items for a picker which shows artifacts with favorites support.
 * @deprecated use ArtifactDropdownProvider instead
 */
export class ArtifactPickerProvider<TArtifact = {}> extends ArtifactPickerProviderBase<TArtifact> {
    constructor(options: IArtifactPickerProviderOptions<TArtifact>) {
        super(options);
    }
}

function isPromise<T>(choice: T | Promise<T>): choice is Promise<T> {
    return typeof (choice as Promise<T>).then === "function";
}
